import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchInput } from '../../../components/search-input';
import { isGenericAiVendor, type Vendor } from '../../../data/vendor';
import { AiReceivingPill } from './ai-receiving-pill';
import { FormatPill } from './format-pill';
import { invoiceFormatLabel } from './invoice-formats';
import { StepHeading } from './step-heading';

export type VendorPickerProps = {
  vendors: Vendor[];
  storeId: string;
  disabled?: boolean;
  onSubmit: (vendorId: string) => Promise<void> | void;
};

export function VendorPicker({ vendors, storeId, disabled = false, onSubmit }: VendorPickerProps) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const eligible = useMemo(
    () =>
      vendors
        .filter((v) => !v.disabled && (v.invoice_formats.length > 0 || v.accepts_ai_csv))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [vendors]
  );

  if (eligible.length === 0) {
    return (
      <div className="rounded-sm border border-gray-200 bg-white p-4 text-sm text-gray-700">
        <p>
          No vendors are set up for invoice receiving yet. Open a vendor in{' '}
          <Link
            to="/stores/$storeId/vendors"
            params={{ storeId }}
            className="text-primary-600 hover:text-primary-800 underline"
          >
            Vendors
          </Link>{' '}
          and choose a Vendor Integration to enable receiving.
        </p>
      </div>
    );
  }

  const visible = (() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return eligible;
    }

    return eligible.filter((v) => v.name.toLowerCase().includes(needle));
  })();

  const handlePick = (vendorId: string) => {
    if (submittingId) {
      return;
    }

    setSubmittingId(vendorId);
    void Promise.resolve(onSubmit(vendorId)).catch(() => {
      setSubmittingId(null);
    });
  };

  const rowsDisabled = disabled || submittingId !== null;

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <StepHeading>Pick a vendor</StepHeading>
      <div className="flex items-center">
        <SearchInput
          inputRef={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={rowsDisabled}
        />
      </div>
      {visible.length === 0 ? (
        <p className="text-sm text-gray-500">No vendors match &ldquo;{query}&rdquo;.</p>
      ) : (
        <div
          role="radiogroup"
          aria-label="Select a vendor"
          className="flex flex-col gap-1 overflow-y-auto min-h-0"
        >
          {visible.map((v) => {
            const isSubmitting = submittingId === v.vendor_id;

            return (
              // biome-ignore lint/a11y/useSemanticElements: radio button with rich content (name + pills) cannot use <input type="radio">
              <button
                key={v.vendor_id}
                type="button"
                role="radio"
                aria-checked={isSubmitting}
                onClick={() => handlePick(v.vendor_id)}
                disabled={rowsDisabled}
                className={`w-full text-left rounded-sm border-2 p-3 transition cursor-pointer disabled:cursor-not-allowed ${
                  isSubmitting
                    ? 'border-primary-500'
                    : 'border-gray-200 hover:border-primary-400 disabled:hover:border-gray-200'
                }`}
              >
                <p className="text-sm font-medium text-gray-900">
                  {v.name}
                  {isSubmitting && (
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      Setting vendor&hellip;
                    </span>
                  )}
                </p>
                <div className="flex gap-1 mt-1">
                  {isGenericAiVendor(v) ? (
                    <AiReceivingPill />
                  ) : (
                    v.invoice_formats.map((f) => (
                      <FormatPill key={f} label={invoiceFormatLabel(f)} />
                    ))
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
